import React, { useEffect, useCallback } from 'react';

import { SpotlightPulse } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Inline } from '@atlaskit/primitives/compiled';

import { expValEquals } from '@confluence/feature-experiments';

import { useLoomScriptReadTimeContext } from '../context/LoomScriptReadTimeContext';

interface LoomScriptReadTimeButtonNudgeWrapperProps {
	children?: Readonly<React.ReactNode>;
}

export const LoomScriptReadTimeButtonNudgeWrapper = ({
	children,
}: LoomScriptReadTimeButtonNudgeWrapperProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isLoomScriptReadTimeButtonNudgeShown, hideLoomScriptReadTimeNudgeAndShowSpotlight } =
		useLoomScriptReadTimeContext();

	// only actually show the nudge if we are in the variation cohort
	const showNudge =
		isLoomScriptReadTimeButtonNudgeShown &&
		expValEquals('loom_ai_script_for_confluence_page_experiment', 'cohort', 'variation');

	const handleLoomScriptReadTimeButtonNudgeRendered = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'loomScriptReadTimeButtonNudge',
				product: 'confluence',
				source: 'loomScriptReadTimeButtonNudge',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const handleLoomScriptReadTimeButtonNudgeClicked = useCallback(() => {
		/**
		 * Dismissal should come from the spotlight that is shown after the nudge button click
		 */
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'loomScriptReadTimeButtonNudge',
				product: 'confluence',
				source: 'loomScriptReadTimeButtonNudge',
			},
		}).fire();
		if (showNudge) {
			hideLoomScriptReadTimeNudgeAndShowSpotlight();
		}
	}, [createAnalyticsEvent, hideLoomScriptReadTimeNudgeAndShowSpotlight, showNudge]);

	useEffect(() => {
		if (showNudge) {
			handleLoomScriptReadTimeButtonNudgeRendered();
		}
	}, [handleLoomScriptReadTimeButtonNudgeRendered, showNudge]);

	return (
		<Inline>
			<SpotlightPulse
				onClick={handleLoomScriptReadTimeButtonNudgeClicked}
				pulse={showNudge}
				radius={4}
			>
				{children}
			</SpotlightPulse>
		</Inline>
	);
};
